import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Tooltip,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import UploadFileIcon from "@mui/icons-material/UploadFile";

import { useDispatch, useSelector } from "react-redux";
import CustomTable from "src/components/Table/Table";
import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

import {
  getSchoolsListAction,
  addSchoolAction,
  deleteSchoolAction,
} from "src/api/schools";

import * as XLSX from "xlsx";

export default function Schools() {
  const dispatch = useDispatch();
  const { schoolsList } = useSelector((state: any) => state.schools);

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
    phone: "",
    email: "",
    latitude: "",
    longitude: "",
  });

  const fetchData = async () => {
    setLoading(true);
    await dispatch(getSchoolsListAction({ pageNo: 1, pageSize: 10 }));
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const confirmDelete = async () => {
    if (!deleteItem) return;
    await dispatch(deleteSchoolAction(deleteItem.id));
    setDeleteItem(null);
    fetchData();
  };

  const handleSubmit = async () => {
    await dispatch(addSchoolAction(form));
    setOpenDialog(false);
    fetchData();
  };

  // ✅ IMPORT EXCEL
  const handleImport = async (e: any) => {
    const file = e.target.files[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data);
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const json: any[] = XLSX.utils.sheet_to_json(sheet);

    setLoading(true);

    for (const row of json) {
      await dispatch(
        addSchoolAction({
          name: row.name || "",
          address: row.address || "",
          city: row.city || "",
          state: row.state || "",
          pincode: row.pincode || "",
          landmark: row.landmark || "",
          phone: row.phone || "",
          email: row.email || "",
          latitude: row.latitude || "",
          longitude: row.longitude || "",
        })
      );
    }

    setLoading(false);
    fetchData();
  };

  const columns = [
    { field: "name", headerName: "School Name", minWidth: 220 },
    { field: "address", headerName: "Address", minWidth: 300 },
    { field: "city", headerName: "City", minWidth: 160 },
    { field: "state", headerName: "State", minWidth: 160 },
    { field: "pincode", headerName: "Pincode", minWidth: 120 },
    { field: "landmark", headerName: "Landmark", minWidth: 260 },
    { field: "phone", headerName: "Phone", minWidth: 150 },
    { field: "email", headerName: "Email", minWidth: 250 },
    { field: "createdOn", headerName: "Created On", minWidth: 150 },
    { field: "location", headerName: "Location", minWidth: 180 },
    { field: "action", headerName: "Action", minWidth: 120 },
  ];

  const actionContent = (column: any, row: any) => {
    if (column === "action") {
      return (
        <IconButton onClick={() => setDeleteItem(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      );
    }

    if (column === "location") {
      const url = `https://www.google.com/maps?q=${row.latitude},${row.longitude}`;

      return (
        <Button
          size="small"
          startIcon={<LocationOnIcon />}
          href={url}
          target="_blank"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            color: "#2563eb",
            whiteSpace: "nowrap",
            minWidth: "150px",
            flexShrink: 0,
          }}
        >
          Show Location
        </Button>
      );
    }

   if (column === "name" || column === "address") {
  return (
        <Tooltip title={row[column] || "-"}>
          <Typography
            noWrap
            sx={{
              maxWidth: column === "name" ? 200 : 280,
            }}
          >
            {row[column] || "-"}
          </Typography>
        </Tooltip>
      );
    }

    return (
      <Typography sx={{ whiteSpace: "nowrap" }}>
        {row[column] || "-"}
      </Typography>
    );
  };

  return (
    <>
      {loading && <CustomBackdrop open />}

      <Box>
        {/* HEADER */}
        <Box sx={{ mt: "30px", p: 3, display: "flex",
          alignItems: "center",
          gap: 2,          // ✅ spacing between buttons
          mb: -2,  }}>

          <Button
            variant="outlined"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setOpenDialog(true)}
          >
            Add School
          </Button>

          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
          >
            Import Excel
            <input
              type="file"
              hidden
              accept=".xlsx, .xls"
              onChange={handleImport}
            />
          </Button>
        </Box>

        {/* TABLE FIXED */}
        <Box
                sx={{
                  "& .MuiTableContainer-root": {
                    overflowX: "auto",
        
                    "&::-webkit-scrollbar": { height: "6px" },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "#f1f5f9",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#cbd5e1",
                      borderRadius: "10px",
                    },
                  },
        
                  "& .MuiTable-root": {
                    minWidth: "1700px",
                  },
                }}
              >

          <CustomTable
            rows={schoolsList?.data || []}
            columns={columns}
            total={schoolsList?.total || 0}
            fetchData={() => {}}
            resetPagination={() => {}}
            action={actionContent}
          />
        </Box>
      </Box>

      {/* ADD */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add School</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            {Object.keys(form).map((key) => (
              <TextField
                key={key}
                label={key}
                value={(form as any)[key]}
                onChange={(e) =>
                  setForm({ ...form, [key]: e.target.value })
                }
              />
            ))}
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)}>
        <DialogTitle>Delete</DialogTitle>
        <DialogContent>
          <Typography>Delete {deleteItem?.name}?</Typography>
          <Button onClick={confirmDelete}>Confirm</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}